import { Part } from "entities/part";
import { BackCounterRequestPartItemAction, RequestOrderItem, RequestPartItemsUnsavedChanges } from "./back-counter.entities";

const generateGuid = (): string => {
  return crypto.randomUUID();
};

const isMatch = (p: RequestOrderItem, part: RequestOrderItem): boolean => {
  return part._id === p._id;
}

export const getMatchedPart = (
  part: RequestOrderItem,
  parts: RequestOrderItem[]
): RequestOrderItem | undefined => parts.find(p => isMatch(p, part));

export const patchRequestPartItems = (
  parts: RequestOrderItem[],
  part?: RequestOrderItem,
  editedParts?: RequestOrderItem[],
  partAddedToRequest?: Part
): RequestOrderItem[] => {

  // Apply core price mapping first if partAddedToRequest is provided
  let processedParts = parts;
  if (partAddedToRequest) {
    processedParts = applyCorePriceMapping(parts, partAddedToRequest);
  }

  // Handle special part logic for partAddedToRequest
  if (partAddedToRequest) {
    processedParts = handleSpecialPartAddition(processedParts, partAddedToRequest);
  }

  if (!part) {
    // Handle the case of one to many parts edited
    return transformParts(processedParts, editedParts);
  }

  // ADD NEW PART
  const addResult = patchPartAdd(part, processedParts);
  if (addResult) {
    return addResult;
  }

  // REMOVE PART
  const removeResult = patchPartRemove(part, processedParts);
  if (removeResult) {
    return removeResult;
  }

  // UPDATE PART
  return processedParts.map((p: RequestOrderItem) => {
    if (isMatch(p, part) && !p.parentId) {
      return { ...part };
    }
    return p;
  });
};

// Clean getUnsavedChanges - ONLY categorizes, no mutations
export const getUnsavedChanges = (
  updatedParts: RequestOrderItem[]
): RequestPartItemsUnsavedChanges => {
  
  // Separate parts by their unsaved changes action
  const partsByAction = categorizePartsByAction(updatedParts);
  
  // Apply bidirectional filtering to handle add/remove cancellation
  const { filteredToAdd, filteredToRemove } = applyBidirectionalFiltering(
    partsByAction.toAdd, 
    partsByAction.toRemove
  );
  
  // Process parts to add (excluding those marked for removal)
  const itemsToAdd = processPartsToAdd(filteredToAdd, updatedParts);
  
  // Process parts to update - NO mutations, just filtering and selection
  const itemsToUpdate = processPartsToUpdate(updatedParts);
  
  return {
    itemsToAdd,
    itemsToUpdate,
    itemsToRemove: filteredToRemove
  };
};

// Move core price mapping to patchRequestPartItems
const applyCorePriceMapping = (parts: RequestOrderItem[], partAddedToRequest: Part): RequestOrderItem[] => {
  return parts.map(part => 
    part.materialNumber === partAddedToRequest?.number
      ? { ...part, coreCharges: partAddedToRequest.corePrice }
      : part
  );
};

// Handle special part addition logic in patchRequestPartItems
const handleSpecialPartAddition = (parts: RequestOrderItem[], partAddedToRequest: Part): RequestOrderItem[] => {
  const specialPart = parts.find(part => 
    part.materialNumber === partAddedToRequest?.rushPartNumber && 
    !part.unsavedChanges
  );
  
  if (!specialPart) {
    return parts;
  }

  return parts.map(part => {
    if (part.materialNumber === specialPart.materialNumber) {
      return {
        ...part,
        unsavedChanges: BackCounterRequestPartItemAction.update,
        reqQuantity: part.orderQuantity
      };
    }
    return part;
  });
};

export const mapCoreOption = (coreOption: string) => {
  if (coreOption === "CORER") {
    return "CORE1";
  } else if (coreOption === "NOCORER") {
    return "NOCORER";
  } else {
    return coreOption;
  }
}

export const transformRequestOrderItemForBuyout = (requestOrderItem: RequestOrderItem, item: { unitPrice: number; vendor: string; purchasePrice: number }): RequestOrderItem => {
  return {
    ...requestOrderItem,
    buyout: 'X',
    vendor: item.vendor,
    purchaseVendor: item.vendor,  
    branchCode: "1001",
    branchName: "San Antonio",
    unitPrice: item.unitPrice,
    hotflag: '',
    sto: '',
    purchasePrice: item.purchasePrice,
  }
}

export const transformRequestOrderItemForSTO = (requestOrderItem: RequestOrderItem, stockTransferOrder: string, quantity: number): RequestOrderItem => {
  return {
    ...requestOrderItem,
    sto: stockTransferOrder ? 'X' : null,
    purchaseVendor: getBranchCode(stockTransferOrder),
    vendor: getBranchCode(stockTransferOrder),
    hotflag: '',
    branchCode: getBranchCode(stockTransferOrder),
    buyout: '',
    orderQuantity: quantity,
    reqQuantity: quantity,
  }
}

export const transformRequestOrderItemForHotFlag = (requestOrderItem: RequestOrderItem, hotflag: string, vendor: string): RequestOrderItem => {
  return {
    ...requestOrderItem,
    hotflag,
    vendor,
    sto: '',
    branchCode: null,
    branchName: null,
    buyout: '',
  }
}

const transformParts = (parts: RequestOrderItem[], editedParts: RequestOrderItem[]) => {
  if (!editedParts?.length) return [...parts];

  // Use a Map for efficient updates
  const partMap = new Map<string | null, RequestOrderItem>();
  parts.forEach(p => partMap.set(p._id, p));

  for (const editedPart of editedParts) {
    const findPartById = partMap.get(editedPart._id);
    const qty = +editedPart.reqQuantity;
    const child = parts.find(f => f.parentId === editedPart._id);

    if (
      findPartById &&
      editedPart.reqQuantity !== findPartById.reqQuantity &&
      qty > editedPart.quantityAvailable
    ) {
      // Use transformByQty logic, but update the map
      const updated = transformByQty(qty, editedPart, Array.from(partMap.values()));
      partMap.clear();
      updated.forEach(p => partMap.set(p._id, p));
    } else if (
      findPartById &&
      editedPart.reqQuantity !== findPartById.reqQuantity &&
      qty < findPartById?.quantityAvailable &&
      child
    ) {
      // Use updateByChild logic, but update the map
      const updated = updateByChild(child, editedPart, Array.from(partMap.values()), qty);
      partMap.clear();
      updated.forEach(p => partMap.set(p._id, p));
    } else if (findPartById) {
      // Direct update with priceVerifyComp enhancement
      const originalPart = parts.find(p => p.materialNumber === editedPart.materialNumber);
      const priceVerifyComp = originalPart?.priceVerifyComp ?? "";
      
      let freight =
        editedPart.freightOverride !== "" && editedPart.freightOverride !== undefined
          ? editedPart.freightOverride
          : editedPart.shipTotal;
      let partQty = editedPart.reqQuantity ?? editedPart.orderQuantity;

      partMap.set(editedPart._id, {
        ...findPartById,
        orderQuantity: partQty,
        reqQuantity: partQty,
        deliveryDate: editedPart.deliveryDate,
        shipTotal: freight,
        freightOverride: freight,
        caseNumber: editedPart.caseNumber,
        deliveryOption: editedPart.deliveryOption,
        unitPrice: editedPart.unitPrice,
        purchasePrice:
          findPartById.material == "PARTSBUYOUTTX" ||
          editedPart.buyout === "X" ||
          editedPart.hotflag === "X"
            ? editedPart.purchasePrice
            : editedPart.unitPrice,
        manualPrice: editedPart.manualPrice,
        hotflag: editedPart.hotflag,
        purchaseVendor: editedPart.purchaseVendor,
        vendor: editedPart.vendor,
        sto: editedPart.sto,
        branchCode: editedPart.branchCode,
        branchName: editedPart.branchName,
        buyout: editedPart.buyout,
        unsavedChanges: editedPart.unsavedChanges ?? BackCounterRequestPartItemAction.update,
        priceVerifyComp // Apply priceVerifyComp here in patchRequestPartItems
      } as RequestOrderItem);
    }
  }

  return Array.from(partMap.values());
}

// Enhanced transformByQty to stamp unsavedChanges when creating new parts
const transformByQty = (qty: number, lastEditedPart: RequestOrderItem, parts: RequestOrderItem[]) => {
  const diff = qty - lastEditedPart.quantityAvailable;
  const newItem: RequestOrderItem = {
    ...lastEditedPart, 
    deliveryOption: "",
    quantityAvailable: 0, 
    orderQuantity: diff, 
    reqQuantity: diff, 
    parentId: lastEditedPart._id, 
    _id: generateGuid(),
    unsavedChanges: BackCounterRequestPartItemAction.add // Stamp as add when creating new part
  };
  
  const child = parts.find(f => f.parentId === lastEditedPart._id);
  if (child) {
    const updatedParts: RequestOrderItem[] = parts.map(m => {
      if (m.parentId === lastEditedPart._id) {
        return {
          ...m, 
          orderQuantity: m.orderQuantity + diff, 
          reqQuantity: m.quantityAvailable + diff,
          unsavedChanges: m.unsavedChanges ?? BackCounterRequestPartItemAction.update,};
      }
      else if (m._id === lastEditedPart._id) {
        return {
          ...m, 
          orderQuantity: m.quantityAvailable, 
          reqQuantity: m.quantityAvailable
        , unsavedChanges: m.unsavedChanges ?? BackCounterRequestPartItemAction.update};
      }
      return m;
    });
    return updatedParts;
  }
  else {
    const updatedParts: RequestOrderItem[] = parts.map(m => {
      if (m._id === lastEditedPart._id && lastEditedPart.quantityAvailable === 0) {
        return {
          ...m, 
          orderQuantity: lastEditedPart.reqQuantity, 
          reqQuantity: lastEditedPart.reqQuantity, 
          deliveryDate: lastEditedPart.deliveryDate,
          unsavedChanges: lastEditedPart.unsavedChanges ?? BackCounterRequestPartItemAction.update
        };
      } else if (m._id === lastEditedPart._id) {
        return {
          ...m, 
          orderQuantity: m.quantityAvailable, 
          reqQuantity: m.quantityAvailable,
          unsavedChanges: m.unsavedChanges ?? BackCounterRequestPartItemAction.update
        };
      } else {
        return m;
      }
    });
    if (lastEditedPart.quantityAvailable !== 0) {
      updatedParts.push(newItem);
    }
    return updatedParts;
  }
}

const updateByChild = (child: RequestOrderItem, lastEditedPart: RequestOrderItem, parts: RequestOrderItem[], qty: number) => {
  const childQty = +child.reqQuantity;
  const diff = lastEditedPart.quantityAvailable - qty;
  if(childQty <= diff) {
    // remove child and update parent
    const updatedParts: RequestOrderItem[] = parts.filter(f => f.parentId !== lastEditedPart._id).map(m => {
      if(m._id === lastEditedPart._id) {
        return {...m, orderQuantity: qty + childQty, reqQuantity: qty + childQty}
      }
      return m;
    })
    return updatedParts;
  }
  else {
    return updateParentAndChild(lastEditedPart, parts, diff);
  }
}

const updateParentAndChild = (lastEditedPart: RequestOrderItem, parts: RequestOrderItem[], diff: number) => {
  const updatedParts: RequestOrderItem[] = parts.map(m => {
    if(m.parentId === lastEditedPart._id) {
      return {...m, orderQuantity: m.orderQuantity - diff, reqQuantity: m.quantityAvailable - diff};
    }
    else if(m._id === lastEditedPart._id) {
      return {...m, orderQuantity: m.quantityAvailable, reqQuantity: m.quantityAvailable};
    }
    return m;
  });
  return updatedParts;
}

const patchPartAddConsolidate = (index: number, parts: RequestOrderItem[], newPart?: RequestOrderItem) => {
  const existingPart = parts[index];
  parts[index] = {
    ...existingPart,
    reqQuantity: +existingPart.reqQuantity + +newPart.reqQuantity,
    orderQuantity: +existingPart.orderQuantity + +newPart.orderQuantity,
    unsavedChanges: existingPart.unsavedChanges ?? BackCounterRequestPartItemAction.update
  };
}

const patchPartExistingSplitOverflow = (index: number, parts: RequestOrderItem[], newPart?: RequestOrderItem) => {
  const existingPart = parts[index];
  parts[index] = {
    ...existingPart,
    reqQuantity: newPart.quantityAvailable,
    orderQuantity: newPart.quantityAvailable,
    unsavedChanges: existingPart.unsavedChanges ?? BackCounterRequestPartItemAction.update
  };

  const leftOverQty = Math.abs(
    Number(newPart.orderQuantity) + Number(existingPart.orderQuantity)
  ) - Number(newPart.quantityAvailable);

  parts.push({
    ...newPart,
    orderQuantity: leftOverQty,
    reqQuantity: leftOverQty,
    quantityAvailable: 0,
    parentId: existingPart._id,
    _id: generateGuid()
  });
}

const splitItem = (parts: RequestOrderItem[], part: RequestOrderItem) => {
  const parentGuid = generateGuid();
  parts.push({
    ...part,
    orderQuantity: +part.quantityAvailable,
    reqQuantity: +part.quantityAvailable,
    _id: parentGuid
  });
  const leftOverQty = Math.abs(+part.orderQuantity - +part.quantityAvailable);
  parts.push({
    ...part,
    orderQuantity: leftOverQty,
    reqQuantity: leftOverQty,
    quantityAvailable: 0,
    parentId: parentGuid,
    _id: generateGuid()
  });
}

const patchPartAdd = (part: RequestOrderItem, parts: RequestOrderItem[]) => {
  if (part.unsavedChanges !== BackCounterRequestPartItemAction.add) {
    return null;
  }

  // Handle special buyout case early
  if (isBuyoutPart(part)) {
    return addBuyoutPart(part, parts);
  }

  const stockInfo = calculateStockInfo(part, parts);
  const existingPartIndex = findExistingPartIndex(part, parts, stockInfo.hasOverStock);

  if (stockInfo.hasOverStock) {
    return handleOverstockedPart(part, parts, existingPartIndex);
  }

  return handleRegularPart(part, parts, existingPartIndex, stockInfo);
}

// Helper: Check if part is a buyout part
const isBuyoutPart = (part: RequestOrderItem): boolean => {
  return part.materialNumber === "PARTSBUYOUTTX";
}

// Helper: Add buyout part directly
const addBuyoutPart = (part: RequestOrderItem, parts: RequestOrderItem[]): RequestOrderItem[] => {
  parts.push({
    ...part, 
    _id: generateGuid(), 
    quantityAvailable: 0
  });
  return parts;
}

// Helper: Calculate stock information
const calculateStockInfo = (part: RequestOrderItem, parts: RequestOrderItem[]) => {
  const partStock = parts
    .filter(x => x.materialNumber === part.materialNumber)
    .reduce((prev, curr) => Number(curr.orderQuantity) + prev, 0);
  
  const hasOverStock = partStock >= part.quantityAvailable;
  const willOverstock = partStock + part.orderQuantity > part.quantityAvailable;

  return { partStock, hasOverStock, willOverstock };
}

// Helper: Find existing part index based on stock status
const findExistingPartIndex = (
  part: RequestOrderItem, 
  parts: RequestOrderItem[], 
  hasOverStock: boolean
): number => {
  return parts.findIndex(x => 
    x.materialNumber === part.materialNumber && 
    x.mainPrItem === part.mainPrItem &&
    (!hasOverStock || x.quantityAvailable === 0)
  );
}

// Helper: Handle overstocked part scenarios
const handleOverstockedPart = (
  part: RequestOrderItem, 
  parts: RequestOrderItem[], 
  existingPartIndex: number
): RequestOrderItem[] => {
  if (existingPartIndex >= 0) {
    patchPartAddConsolidate(existingPartIndex, parts, part);
  } else {
    parts.push({
      ...part, 
      _id: generateGuid(), 
      quantityAvailable: 0
    });
  }
  return parts;
}

// Helper: Handle regular (not overstocked) part scenarios
const handleRegularPart = (
  part: RequestOrderItem, 
  parts: RequestOrderItem[], 
  existingPartIndex: number,
  stockInfo: { willOverstock: boolean }
): RequestOrderItem[] => {
  if (existingPartIndex >= 0) {
    if (stockInfo.willOverstock) {
      patchPartExistingSplitOverflow(existingPartIndex, parts, part);
    } else {
      patchPartAddConsolidate(existingPartIndex, parts, part);
    }
  } else if (part.orderQuantity > part.quantityAvailable) {
    splitItem(parts, part);
  } else {
    parts.push({
      ...part, 
      _id: generateGuid()
    });
  }
  return parts;
}

const patchPartRemove = (part: RequestOrderItem, parts: RequestOrderItem[]) => {
  if (part.unsavedChanges === BackCounterRequestPartItemAction.remove) {
    if( part.itemNumber){
      return parts.map((p: RequestOrderItem) => isMatch(p, part) ? { ...p, unsavedChanges: BackCounterRequestPartItemAction.remove } : p);
    } else {
      return parts.filter((p: RequestOrderItem) => !isMatch(p, part));
    }
  } else {
    return null;
  }
}

const getBranchCode = (stockTransferOrder: string) => {
  const splittedBranchData = stockTransferOrder?.split('-');
  if(splittedBranchData?.length > 0) {
    return splittedBranchData[0];
  }
  return stockTransferOrder ?? "";
}

// Simplified helper functions for getUnsavedChanges - NO mutations
const categorizePartsByAction = (parts: RequestOrderItem[]) => {
  return parts.reduce(
    (acc, part) => {
      switch (part.unsavedChanges) {
        case BackCounterRequestPartItemAction.add:
          acc.toAdd.push(part);
          break;
        case BackCounterRequestPartItemAction.remove:
          acc.toRemove.push(part);
          break;
        default:
          acc.other.push(part);
      }
      return acc;
    },
    { toAdd: [], toRemove: [], other: [] } as {
      toAdd: RequestOrderItem[];
      toRemove: RequestOrderItem[];
      other: RequestOrderItem[];
    }
  );
};

// New bidirectional filtering logic
const applyBidirectionalFiltering = (
  partsToAdd: RequestOrderItem[],
  partsToRemove: RequestOrderItem[]
) => {
  // Create Sets for efficient lookup by _id
  const addIds = new Set(partsToAdd.map(p => p._id));
  const removeIds = new Set(partsToRemove.map(p => p._id));
  
  // Filter out parts that exist in both lists (cancellation logic)
  const filteredToAdd = partsToAdd.filter(part => !removeIds.has(part._id));
  const filteredToRemove = partsToRemove.filter(part => !addIds.has(part._id));
  
  return {
    filteredToAdd,
    filteredToRemove
  };
};

// Simplified processPartsToAdd - no longer needs to handle removal filtering
const processPartsToAdd = (
  partsToAdd: RequestOrderItem[],
  updatedParts: RequestOrderItem[]
): RequestOrderItem[] => {
  return partsToAdd.map(part => {
    const updatedPart = updatedParts.find(updated => updated._id === part._id);
    return updatedPart ?? part;
  });
};

// Updated processPartsToUpdate to use filtered lists
const processPartsToUpdate = (
  updatedParts: RequestOrderItem[]
): RequestOrderItem[] => {
 
  // Filter using the already-filtered add/remove lists
  return updatedParts.filter(part => part.unsavedChanges === BackCounterRequestPartItemAction.update);
};
